/**
 * Tiny typed fetch wrapper. Same-origin via Vite proxy in dev; same-origin in prod.
 * All requests carry credentials so the HttpOnly session cookie flows.
 */

export type Me =
  | { signed_in: false }
  | {
      signed_in: true;
      blazerid: string;
      is_admin: boolean;
      access_code_verified: boolean;
    };

// Structured multi-part parts (Flavor 2). Per-part `answer` and `tolerance`
// are stripped from the served shape; only label/prompt/type (and `choices`
// for choice parts) reach the student. Admin detail carries the full part
// definition.
export type ServedPart =
  | { label: string; prompt: string; type: "numeric" }
  | { label: string; prompt: string; type: "choice"; choices: string[] }
  | { label: string; prompt: string; type: "text" };

// Per-part grading result returned by /api/practice/answer when the
// question carries parts_json.
export interface PartResult {
  label: string;
  type: "numeric" | "choice" | "text";
  correct: boolean | null;       // null for text parts pending model grading
  score: number;
  method: string;
  student_answer: string;
  answer: string;
  explanation: string;
  choices?: string[];
}

export interface ServedQuestion {
  id: number;
  node_slug: string;
  node_canonical_name: string;
  // Short student-facing label for the question card header. NULL falls
  // back to canonical_name. Scope picker + admin keep canonical_name.
  node_display_label: string | null;
  topic_name: string | null;
  topic_slug: string | null;
  difficulty: "intro" | "intermediate" | "harder";
  response_format: "multiple_choice" | "single_numeric" | "multi_part" | "free_text";
  cognitive_type: "computational" | "conceptual";
  variety_axis: string | null;
  sub_skill_id: string | null;
  question: string;
  choices: string[] | null;
  assumptions: string[] | null;
  parts: ServedPart[] | null;
  verified: boolean;
}

export interface CoverageSubSkill {
  id: string;
  description: string;
}

export interface CoverageConcept {
  concept_id: string;
  name: string;
  n_questions: number;
  sub_skills: CoverageSubSkill[];
}

export interface CoverageTopic {
  slug: string;
  name: string;
  n_questions: number;
  concepts: CoverageConcept[];
}

export interface CoverageTree {
  subject: { slug: string; name: string };
  topics: CoverageTopic[];
}

// ---- Admin types ----

export interface AdminUsageRow {
  blazerid: string;
  is_admin: boolean;
  attempts: number;
  correct: number;
  pct_correct: number | null;
  avg_time_ms: number | null;
  last_active: string | null;
  sessions: number;
  votes: number;
  comments: number;
}

export interface AdminQuestionRow {
  question_id: number;
  topic_name: string | null;
  concept_name: string;
  response_format: string;
  difficulty: string;
  bank_source: string | null;
  status: string;
  served: number;
  answered: number;
  correct: number;
  pct_correct: number | null;
  skipped: number;
  skip_rate: number | null;
  upvotes: number;
  downvotes: number;
  avg_time_ms: number | null;
  n_comments: number;
}

export interface AdminCommentRow {
  id: number;
  blazerid: string | null;
  question_id: number;
  concept_name: string;
  body: string;
  created_at: string;
}

export interface AdminBankDepth {
  questions: { total: number; from_bank: number; generated: number };
  answers_by_source: { from_bank: number; generated: number };
  generations: {
    attempts: { last_24h: number; last_7d: number; total: number };
    successes: { last_24h: number; last_7d: number; total: number };
    budget: number;
    budget_exhausted_events: number;
  };
}

export interface AdminFormatDemandRow {
  topic_slug: string | null;
  topic_name: string | null;
  format: string;
  students: number;
  selections: number;
  served: number;
}

export interface AdminQuestionDetail {
  question: {
    id: number;
    response_format: string;
    cognitive_type: string | null;
    variety_axis: string | null;
    difficulty: string;
    topic_name: string | null;
    topic_slug: string | null;
    concept_name: string;
    concept_slug: string;
    sub_skill_id: string | null;
    bank_source: string | null;
    status: string;
    question: string;
    answer_key: string;
    worked_solution: string;
    choices: string[] | null;
    distractor_rationales: DistractorRationales;
    assumptions: string[] | null;
    // Admin sees the FULL part definitions, including each part's
    // canonical answer + numeric tolerance. The serve path strips those.
    parts: {
      label: string;
      prompt: string;
      type: "numeric" | "choice" | "text";
      answer: string;
      choices?: string[];
      tolerance?: number;
    }[] | null;
  };
  stats: {
    served: number;
    answered: number;
    correct: number;
    skipped: number;
    pct_correct: number | null;
    skip_rate: number | null;
    avg_time_ms: number | null;
    upvotes: number;
    downvotes: number;
  };
  attempts: {
    blazerid: string | null;
    answered_at: string;
    correct: boolean;
    answer: string | null;
    time_ms: number | null;
    method: string | null;
  }[];
  comments: {
    id: number;
    blazerid: string | null;
    body: string;
    created_at: string;
  }[];
}

// ---- Student dashboard ----

export interface MeStatsConcept {
  concept_name: string;
  topic_name: string | null;
  attempts: number;
  correct: number;
  pct_correct: number | null;
}

export interface MeStatsRecent {
  at: string;
  question_id: number;
  correct: boolean;
  time_ms: number | null;
  concept_name: string;
}

export interface MeStats {
  blazerid: string;
  attempts: number;
  correct: number;
  pct_correct: number | null;
  avg_time_ms: number | null;
  sessions: number;
  first_active: string | null;
  last_active: string | null;
  by_concept: MeStatsConcept[];
  recent: MeStatsRecent[];
}

// The bank's distractor_rationales is a dict keyed by choice letter (A/B/C/D)
// mapping to a two-field record explaining why a student might pick that
// option and why it is wrong. Older / hand-authored questions in the same
// table may use simpler shapes (array of strings, dict of strings); the
// renderer normalizes all known shapes.
export interface RichRationale {
  why_picked?: string;
  why_wrong?: string;
}

export type DistractorRationales =
  | string[]
  | { [letter: string]: string }
  | { [letter: string]: RichRationale }
  | null;

export interface AnswerResult {
  correct: boolean;
  score: number;
  answer_key: string;
  worked_solution: string;
  choices: string[] | null;
  distractor_rationales: DistractorRationales;
  method: string;
  // Per-part feedback when the question used the parts-array path; null
  // for everything else.
  part_results: PartResult[] | null;
}

export interface VoteCounts {
  upvotes: number;
  downvotes: number;
  my_vote?: number; // only present on GET
}

class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, detail, `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // ----- auth -----
  me: () => http<Me>("/api/auth/me"),
  checkAccessCode: (code: string) =>
    http<{ ok: true }>("/api/auth/check-access-code", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  enter: (code: string, blazerid: string) =>
    http<{ ok: true; blazerid: string }>("/api/auth/enter", {
      method: "POST",
      body: JSON.stringify({ code, blazerid }),
    }),
  logout: () => http<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  // ----- practice -----
  // Scope is concept/topic only — chapter is NOT a serving dimension.
  // `format` is the student-facing question-type filter; "all" omits the
  // filter entirely so the API returns mixed formats by default.
  next: (params: {
    topic_slug?: string;
    concept_id?: string;
    sub_skill_id?: string;
    format?: "mc" | "fill_in";
    exclude_ids?: number[];
    exclude_recent_sub_skills?: string[];
  }) => {
    const qs = new URLSearchParams();
    if (params.topic_slug) qs.set("topic_slug", params.topic_slug);
    if (params.concept_id) qs.set("concept_id", params.concept_id);
    if (params.sub_skill_id) qs.set("sub_skill_id", params.sub_skill_id);
    if (params.format) qs.set("format", params.format);
    if (params.exclude_ids?.length) qs.set("exclude_ids", params.exclude_ids.join(","));
    if (params.exclude_recent_sub_skills?.length)
      qs.set("exclude_recent_sub_skills", params.exclude_recent_sub_skills.join(","));
    const url = `/api/practice/next${qs.toString() ? "?" + qs : ""}`;
    return http<ServedQuestion>(url);
  },
  coverageTree: () => http<CoverageTree>("/api/coverage/tree"),

  // ----- admin -----
  adminUsage: (includeDemo: boolean) =>
    http<{ rows: AdminUsageRow[] }>(`/api/admin/usage${includeDemo ? "?include_demo=1" : ""}`),
  adminQuestions: (includeDemo: boolean, limit = 200) =>
    http<{ rows: AdminQuestionRow[] }>(
      `/api/admin/questions?limit=${limit}${includeDemo ? "&include_demo=1" : ""}`,
    ),
  adminComments: (includeDemo: boolean, limit = 200) =>
    http<{ rows: AdminCommentRow[] }>(
      `/api/admin/comments?limit=${limit}${includeDemo ? "&include_demo=1" : ""}`,
    ),
  adminFormatDemand: (includeDemo: boolean) =>
    http<{ rows: AdminFormatDemandRow[] }>(
      `/api/admin/format-demand${includeDemo ? "?include_demo=1" : ""}`,
    ),
  adminExportCsvUrl: (includeDemo: boolean) =>
    `/api/admin/export.csv${includeDemo ? "?include_demo=1" : ""}`,
  adminQuestionDetail: (qid: number, includeDemo: boolean) =>
    http<AdminQuestionDetail>(
      `/api/admin/questions/${qid}${includeDemo ? "?include_demo=1" : ""}`,
    ),
  adminBankDepth: () => http<AdminBankDepth>("/api/admin/bank-depth"),
  adminResetPilotData: () =>
    http<{
      deleted: Record<string, number>;
      preserved: Record<string, number>;
      backup_filename: string;
    }>("/api/admin/reset-pilot-data", { method: "POST" }),
  adminSetQuestionStatus: (qid: number, status: "active" | "retired") =>
    http<{
      qid: number;
      old_status: string;
      new_status: string;
      changed: boolean;
    }>(`/api/admin/questions/${qid}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),
  adminBackupDbUrl: () => "/api/admin/backup.db",
  // Admin preview — render any qid through the student components without
  // polluting pilot stats. No events emitted on either call.
  adminQuestionPreview: (qid: number) =>
    http<ServedQuestion>(`/api/admin/questions/${qid}/preview`),
  adminQuestionPreviewGrade: (qid: number, answer: string) =>
    http<AnswerResult & { is_preview: true }>(
      `/api/admin/questions/${qid}/preview-grade`,
      { method: "POST", body: JSON.stringify({ answer }) },
    ),

  // ----- student dashboard -----
  meStats: () => http<MeStats>("/api/me/stats"),
  answer: (question_id: number, answer: string, time_on_question_ms?: number) =>
    http<AnswerResult>("/api/practice/answer", {
      method: "POST",
      body: JSON.stringify({ question_id, answer, time_on_question_ms }),
    }),
  vote: (question_id: number, value: -1 | 0 | 1) =>
    http<VoteCounts>("/api/practice/vote", {
      method: "POST",
      body: JSON.stringify({ question_id, value }),
    }),
  voteCounts: (question_id: number) => http<VoteCounts>(`/api/practice/votes/${question_id}`),
  comment: (question_id: number, body: string) =>
    http<{ ok: true }>("/api/practice/comment", {
      method: "POST",
      body: JSON.stringify({ question_id, body }),
    }),
  skip: (question_id: number, time_on_question_ms?: number) =>
    http<{ ok: true }>("/api/practice/skip", {
      method: "POST",
      body: JSON.stringify({ question_id, time_on_question_ms }),
    }),
  event: (type: string, opts?: { question_id?: number; payload?: object }) =>
    http<{ ok: true }>("/api/events", {
      method: "POST",
      body: JSON.stringify({ type, ...opts }),
    }),
};

export { ApiError };
